import useSWR from "swr";

import * as fetcher from "../fetcher";

export const DEV_ROLE_GROUP_ID = "5cbaf2c2-cead-43de-b620-473f2535cc0c";
export const SUPER_USER_GROUP_ID = "b33beb27-1bcc-4ce8-81e8-73bd1a03c2c9";

const DEV_OR_SUPER_USER_GROUP_IDS: readonly string[] = [
  DEV_ROLE_GROUP_ID,
  SUPER_USER_GROUP_ID,
];

const targetIds = new Set(
  DEV_OR_SUPER_USER_GROUP_IDS.map((id) => id.toLocaleLowerCase())
);

interface UserGroup {
  id: string;
}

interface UserGroupsResponse {
  value: UserGroup[];
  "@odata.nextLink"?: string;
}

const MEMBER_OF_PATH = "/me/memberOf?$select=id";
const GRAPH_BASE_URL = "https://graph.microsoft.com/v1.0";
const MAX_PAGES = 100;

const getNextPagePath = (nextLink: string): string => {
  const nextUrl = new URL(nextLink);
  const graphUrl = new URL(GRAPH_BASE_URL);

  if (
    nextUrl.origin !== graphUrl.origin ||
    !nextUrl.pathname.startsWith(`${graphUrl.pathname}/`)
  ) {
    throw new Error("Microsoft Graph returned an invalid pagination link");
  }

  return `${nextUrl.pathname.slice(graphUrl.pathname.length)}${nextUrl.search}`;
};

const fetchIsDevOrSuperUser = async (): Promise<boolean> => {
  let pagePath: string | undefined = MEMBER_OF_PATH;

  for (let page = 0; page < MAX_PAGES && pagePath; page += 1) {
    const response: UserGroupsResponse =
      await fetcher.graph.get<UserGroupsResponse>(pagePath);

    if (!Array.isArray(response.value)) {
      throw new Error(
        "Microsoft Graph returned an invalid membership response"
      );
    }

    if (
      response.value.some(
        (group) =>
          typeof group.id === "string" &&
          targetIds.has(group.id.toLocaleLowerCase())
      )
    ) {
      return true;
    }

    pagePath = response["@odata.nextLink"]
      ? getNextPagePath(response["@odata.nextLink"])
      : undefined;
  }

  return false;
};

export const useIsDevOrSuperUser = (): boolean => {
  const { data } = useSWR<boolean>(
    "useIsDevOrSuperUser:/me/memberOf",
    fetchIsDevOrSuperUser,
    {
      errorRetryCount: 3,
      revalidateOnFocus: false,
      dedupingInterval: 10 * 60 * 1000,
    }
  );
  return Boolean(data);
};
